import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Cookie } from 'lucide-react';
import { Link } from 'react-router-dom';

export const CookieBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [preferences, setPreferences] = useState({
    analytics: true,
    preferences: true,
    marketing: false
  });

  useEffect(() => {
    // Vérifier si l'utilisateur a déjà accepté les cookies
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // Attendre 1 seconde avant d'afficher
      const timer = setTimeout(() => {
        setIsVisible(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      analytics: true,
      preferences: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      analytics: false,
      preferences: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem('cookieConsent', JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const [showDetails, setShowDetails] = useState(false);

  return (
    <AnimatePresence>
      {isVisible && (
        <>
          {/* Backdrop */}
          {showDetails && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowDetails(false)}
              className="fixed inset-0 bg-black/50 z-40"
            />
          )}

          {/* Banner */}
          <motion.div
            initial={{ opacity: 0, y: 100 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 100 }}
            transition={{ duration: 0.3 }}
            className="fixed bottom-0 left-0 right-0 z-50 p-4 lg:p-6"
          >
            <div className="container mx-auto max-w-6xl">
              {!showDetails ? (
                // Vue simple
                <div className="bg-gradient-to-r from-[#1a1f2b] via-[#212635] to-[#1a1f2b] border border-blue-500/30 rounded-2xl p-6 lg:p-8 shadow-2xl">
                  <div className="flex items-start justify-between gap-6">
                    <div className="flex items-start gap-4 flex-1">
                      <Cookie className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                      <div>
                        <h3 className="text-lg font-montserrat font-bold text-white mb-2">
                          Gestion des Cookies
                        </h3>
                        <p className="text-gray-300 text-sm">
                          Nous utilisons des cookies pour améliorer votre expérience. 
                          <Link to="/privacy" className="text-blue-400 hover:text-blue-300 mx-1">
                            En savoir plus
                          </Link>
                        </p>
                      </div>
                    </div>

                    {/* Boutons */}
                    <div className="flex gap-3 flex-col lg:flex-row flex-shrink-0">
                      <button
                        onClick={() => setShowDetails(true)}
                        className="px-4 py-2 text-gray-300 hover:text-white border border-gray-600 rounded-lg font-semibold text-sm transition-colors"
                      >
                        Paramètres
                      </button>
                      <button
                        onClick={handleRejectAll}
                        className="px-4 py-2 text-gray-300 hover:text-white border border-gray-600 rounded-lg font-semibold text-sm transition-colors"
                      >
                        Refuser
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleAcceptAll}
                        className="px-6 py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold text-sm hover:shadow-lg hover:shadow-blue-500/50 transition-all"
                      >
                        Accepter tout
                      </motion.button>
                    </div>
                  </div>
                </div>
              ) : (
                // Vue détaillée
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="bg-[#181C25] border border-blue-500/30 rounded-2xl p-8 shadow-2xl max-w-2xl mx-auto"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-2xl font-montserrat font-bold text-white">
                      Préférences de Cookies
                    </h3>
                    <button
                      onClick={() => setShowDetails(false)}
                      className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
                    >
                      <X size={24} className="text-gray-400" />
                    </button>
                  </div>

                  {/* Kontinu */}
                  <p className="text-gray-300 mb-8 text-sm">
                    Nous respectons votre vie privée. Vous pouvez choisir les cookies à utiliser.
                  </p>

                  {/* Cookies toggles */}
                  <div className="space-y-4 mb-8">
                    {/* Essentiels (toujours activés) */}
                    <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-white">Cookies Essentiels</h4>
                          <p className="text-gray-400 text-sm mt-1">
                            Nécessaires au fonctionnement du site (toujours activés)
                          </p>
                        </div>
                        <div className="w-12 h-7 bg-green-600 rounded-full flex items-center px-1">
                          <div className="w-5 h-5 bg-white rounded-full ml-auto" />
                        </div>
                      </div>
                    </div>

                    {/* Analytics */}
                    <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-white">Analytics</h4>
                          <p className="text-gray-400 text-sm mt-1">
                            Nous aident à comprendre comment vous utilisez le site
                          </p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                          className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                            preferences.analytics ? 'bg-blue-600' : 'bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences.analytics ? 'ml-auto' : ''
                          }`} />
                        </button>
                      </div>
                    </div>

                    {/* Préférences */}
                    <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-white">Préférences</h4>
                          <p className="text-gray-400 text-sm mt-1">
                            Sauvegardent vos choix et préférences
                          </p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, preferences: !p.preferences }))}
                          className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                            preferences.preferences ? 'bg-blue-600' : 'bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences.preferences ? 'ml-auto' : ''
                          }`} />
                        </button>
                      </div>
                    </div>

                    {/* Marketing */}
                    <div className="p-4 rounded-lg bg-gray-800/50 border border-gray-700">
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-semibold text-white">Marketing</h4>
                          <p className="text-gray-400 text-sm mt-1">
                            Utilisés pour des contenus personnalisés et des publicités
                          </p>
                        </div>
                        <button
                          onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                          className={`w-12 h-7 rounded-full flex items-center px-1 transition-colors ${
                            preferences.marketing ? 'bg-blue-600' : 'bg-gray-600'
                          }`}
                        >
                          <div className={`w-5 h-5 bg-white rounded-full transition-transform ${
                            preferences.marketing ? 'ml-auto' : ''
                          }`} />
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Boutons */}
                  <div className="flex gap-3 flex-col sm:flex-row">
                    <button
                      onClick={handleRejectAll}
                      className="px-6 py-3 text-gray-300 hover:text-white border border-gray-600 rounded-lg font-semibold transition-colors flex-1"
                    >
                      Refuser tout
                    </button>
                    <button
                      onClick={handleSavePreferences}
                      className="px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-blue-500/50 transition-all flex-1"
                    >
                      Enregistrer mes préférences
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={handleAcceptAll}
                      className="px-6 py-3 bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg font-semibold hover:shadow-lg hover:shadow-green-600/50 transition-all flex-1"
                    >
                      Accepter tout
                    </motion.button>
                  </div>
                </motion.div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CookieBanner;
